import React, { FunctionComponent } from "react";

import cls from "classnames";
import moment from "moment";
import { Modal, Tag, Button, Alert } from "antd";

import { CourseCounters } from "./courseCountersUtils";

import styles from "./progressAudit.module.scss";

const EPSILON = 1e-6;

interface CertificateInfo {
  issued: boolean;
  createdAt?: string;
  hash?: string;
}

interface CountersModalProps {
  visible: boolean;
  onClose: () => void;
  counters: CourseCounters | null;
  evaluationTitles: Record<string, string>;
  takenLessonQuizIds: Set<string>;
  isLoadingDetail: boolean;
  certificate: CertificateInfo | null;
  onFixLesson: (progressLessonSubdocId: string) => void;
  fixingLessonSubdocId: string | null;
  onFixTopicEvaluation: (topicId: string, evaluationId: string) => void;
  fixingTopicId: string | null;
  onFixCertificate: () => void;
  isFixingCertificate: boolean;
  onFixCourse: () => void;
  isFixingCourse: boolean;
}

const PassTag: FunctionComponent<{ passed: boolean }> = ({ passed }) => (
  <Tag color={passed ? "green" : "red"}>{passed ? "Aprobado" : "No aprobado"}</Tag>
);

export const CountersModal: FunctionComponent<CountersModalProps> = ({
  visible,
  onClose,
  counters,
  evaluationTitles,
  takenLessonQuizIds,
  isLoadingDetail,
  certificate,
  onFixLesson,
  fixingLessonSubdocId,
  onFixTopicEvaluation,
  fixingTopicId,
  onFixCertificate,
  isFixingCertificate,
  onFixCourse,
  isFixingCourse,
}) => {
  if (!counters) {
    return null;
  }

  const missingItems = counters.totalItems - counters.completedItems;
  // A finished course (courseProgress.progress ~ 1) should always have a
  // certificate - service-learning only sets finishedAt/progress:1 once
  // createCertificate succeeds. `certificate` is null while still loading,
  // so this only fires once we actually know it's missing. Also requires
  // hasCertificateTemplate: course.certificate is optional, so a finished
  // course that was never certificate-enabled legitimately has none.
  const isMissingCertificate =
    counters.isFinished && counters.hasCertificateTemplate && !isLoadingDetail && !!certificate && !certificate.issued;
  // Same idea as isMissingCertificate, but for the exam itself: makeProgress
  // only accepts courseProgress.progress:1 once examPassed is true (when the
  // course has its own exam) - so a finished course whose exam isn't in
  // evaluationsPassed is the same class of data inconsistency.
  const isMissingExam = counters.isFinished && counters.hasExam && !counters.examPassed;
  // General case: computeCourseCounters marks status "failed" exactly
  // when isFinished but computedPercentage isn't really 100% - a topic
  // test or a lesson that isn't really approved despite the topic/course
  // being marked finished. isMissingExam is one specific cause of this
  // (and already has its own, more specific alert above), so this one is
  // gated to skip firing a redundant second alert for that same cause.
  const isMissingPercentage = counters.status === "failed" && !isMissingExam;
  // The inverse case: every lesson/topic test is really approved
  // (computedPercentage is really 100%) but the course itself never got
  // marked finished (courseProgress.progress stays 0/never reached 1) -
  // the client apparently never sent the final {courseId, progress:1}
  // after finishing the last topic. None of the alerts above catch this,
  // since they all require isFinished to be true first.
  const isMissingCourseFinish = !counters.isFinished && counters.computedPercentage > 1 - EPSILON;

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      onOk={onClose}
      title="Detalle de Counters"
      width={720}
    >
      <div className={styles.countersModalCourseTitle}>{counters.courseTitle}</div>

      {isMissingCertificate && (
        <Alert
          className={styles.countersModalDataAlert}
          type="error"
          showIcon
          message="El curso figura terminado (progress: 1) pero no tiene certificado emitido."
          action={
            <Button size="small" danger loading={isFixingCertificate} onClick={onFixCertificate}>
              Crear certificado
            </Button>
          }
        />
      )}

      {isMissingExam && (
        <Alert
          className={styles.countersModalDataAlert}
          type="error"
          showIcon
          message="El curso figura terminado (progress: 1) pero el examen final no está aprobado."
        />
      )}

      {isMissingPercentage && (
        <Alert
          className={styles.countersModalDataAlert}
          type="error"
          showIcon
          message={`El curso figura terminado (progress: 1) pero el porcentaje calculado es ${(counters.computedPercentage * 100).toFixed(1)}%, no 100% (faltan lecciones o tests de módulo por aprobar).`}
        />
      )}

      {isMissingCourseFinish && (
        <Alert
          className={styles.countersModalDataAlert}
          type="error"
          showIcon
          message="Todas las lecciones y tests de módulo están aprobados (100%) pero el curso nunca quedó marcado como terminado (courseProgress.progress sigue en 0) - probablemente el cliente nunca envió la confirmación final del curso."
          action={
            <Button size="small" danger loading={isFixingCourse} onClick={onFixCourse}>
              Marcar curso terminado
            </Button>
          }
        />
      )}

      <div className={styles.countersModalSummary}>
        <div>
          Porcentaje calculado: <strong>{(counters.computedPercentage * 100).toFixed(1)}%</strong>
        </div>
        <div>
          Ítems completados: <strong>{counters.completedItems}</strong> de{" "}
          <strong>{counters.totalItems}</strong>
          {missingItems > 0 && (
            <span> — faltan {missingItems} para el 100%</span>
          )}
        </div>
      </div>

      <div className={styles.countersModalSection}>
        <div className={styles.countersModalSectionTitle}>
          Curso
          <Tag className={styles.countersModalTopicProgressTag} color={counters.isFinished ? "green" : "default"}>
            progress: {counters.isFinished ? "1" : "< 1"}
          </Tag>
        </div>
        <div className={styles.countersModalRow}>
          <span>Examen final</span>
          {counters.hasExam ? (
            <PassTag passed={counters.examPassed} />
          ) : (
            <Tag>No tiene</Tag>
          )}
        </div>
        <div className={styles.countersModalRow}>
          <span>Encuesta (no cuenta para el %)</span>
          {counters.hasSurvey ? (
            <Tag color={counters.surveyPassed ? "green" : "default"}>
              {counters.surveyPassed ? "Respondida" : "No respondida"}
            </Tag>
          ) : (
            <Tag>No tiene</Tag>
          )}
        </div>
        <div className={styles.countersModalRow}>
          <span>Certificado</span>
          {certificate ? (
            <span className={styles.countersModalRowActions}>
              <Tag color={certificate.issued ? "green" : "default"}>
                {certificate.issued
                  ? `Emitido${certificate.createdAt ? ` (${moment(certificate.createdAt).format("DD/MM/YYYY")})` : ""}`
                  : "No emitido"}
              </Tag>
              {certificate.issued && certificate.hash && (
                <a href={`/public/certificate/${certificate.hash}`} target="_blank" rel="noopener noreferrer">
                  Abrir
                </a>
              )}
            </span>
          ) : (
            <Tag>{isLoadingDetail ? "Cargando…" : "Sin datos"}</Tag>
          )}
        </div>
      </div>

      {counters.topics.map((topic) => (
        <div key={topic.topicId} className={styles.countersModalSection}>
          <div className={styles.countersModalSectionTitle}>
            {topic.title}
            <Tag className={styles.countersModalTopicProgressTag} color={topic.markedFinished ? "green" : "default"}>
              progress: {topic.markedFinished ? "1" : "< 1"}
            </Tag>
          </div>
          <div className={styles.countersModalRow}>
            <span>Lecciones aprobadas</span>
            <span
              className={cls({
                [styles.countersModalMismatch]: topic.lessonsApproved !== topic.lessonsRequired,
              })}
            >
              {topic.lessonsApproved}/{topic.lessonsRequired}
            </span>
          </div>
          <div className={styles.countersModalRow}>
            <span>Test del módulo</span>
            <span className={styles.countersModalRowActions}>
              {topic.hasTest ? <PassTag passed={topic.testPassed} /> : <Tag>No tiene</Tag>}
              {topic.markedFinished && topic.hasTest && !topic.testPassed && topic.testEvaluationId && (
                <Button
                  size="small"
                  type="link"
                  loading={fixingTopicId === topic.topicId}
                  disabled={!!fixingTopicId && fixingTopicId !== topic.topicId}
                  onClick={() => onFixTopicEvaluation(topic.topicId, topic.testEvaluationId as string)}
                >
                  Fix
                </Button>
              )}
            </span>
          </div>
          <table className={styles.countersModalTable}>
            <thead>
              <tr>
                <th>Lección</th>
                <th>Aprobada</th>
                <th>Quiz</th>
                <th>Tomado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {topic.lessons.map((lesson) => (
                <tr key={lesson.lessonId}>
                  <td>{lesson.title}</td>
                  <td>
                    <Tag color={lesson.approved ? "green" : "red"}>
                      {lesson.approved ? "Sí" : "No"}
                    </Tag>
                  </td>
                  <td>
                    {lesson.hasQuiz ? (
                      <Tag>
                        {lesson.quizEvaluationId ? evaluationTitles[lesson.quizEvaluationId] || "Sí" : "Sí"}
                      </Tag>
                    ) : (
                      <Tag>No tiene</Tag>
                    )}
                  </td>
                  <td>
                    {lesson.hasQuiz ? (
                      isLoadingDetail ? (
                        <Tag>Cargando…</Tag>
                      ) : (
                        <Tag color={lesson.quizEvaluationId && takenLessonQuizIds.has(lesson.quizEvaluationId) ? "green" : "default"}>
                          {lesson.quizEvaluationId && takenLessonQuizIds.has(lesson.quizEvaluationId) ? "Sí" : "No"}
                        </Tag>
                      )
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {topic.markedFinished && !lesson.approved && lesson.progressLessonSubdocId && (
                      <Button
                        size="small"
                        type="link"
                        loading={fixingLessonSubdocId === lesson.progressLessonSubdocId}
                        disabled={!!fixingLessonSubdocId && fixingLessonSubdocId !== lesson.progressLessonSubdocId}
                        onClick={() => onFixLesson(lesson.progressLessonSubdocId as string)}
                      >
                        Fix
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </Modal>
  );
};
